import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import TitlePages from "../../../layout/TitlePages";
import Breadcrumbs from "../../../layout/Breadcrumbs";
import Expandible from "../../../layout/HelperDataTable/Expandible";
import { useCompleteTableConfig } from "../../../constants";
import { dataAcuerdosINE } from "../../../data/dataAcuerdos";
import {
  AcuerdosDetailContainer,
  AcuerdosDetailSection,
  AcuerdosDetailRow,
} from "../../../components/acuerdos/AcuerdosDetail";

const baseUrl = "https://itetlax.org.mx/assets/pdf/acuerdos/INE/";

const FileLink = ({ baseUrl, year, url, type }) => {
  const icon = type === "pdf" ? faFilePdf : faFileExcel;
  const className = type === "pdf" ? "btn btn-danger" : "btn btn-success";
  const fullUrl = `${baseUrl}${year}/${url}`;

  return (
    <a href={fullUrl} target="_blank" rel="noreferrer">
      <FontAwesomeIcon icon={icon} className={className} />
    </a>
  );
};

const TableRow = ({ baseUrl, year, title, url, type }) =>
  title && url ? (
    <tr>
      <td>{title.toUpperCase()}</td>
      <td>
        <FileLink baseUrl={baseUrl} year={year} url={url} type={type} />
      </td>
    </tr>
  ) : null;

const AcuerdosTableINE = ({ year }) => {
  const data = useMemo(() => dataAcuerdosINE[year] || [], [year]);

  useEffect(() => {
    document.title = `Acuerdos INE ${year}`;
  }, [year]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "numDoc",
        header: "ACUERDO",
        footer: "ACUERDO",
        size: 55,
      },
      {
        accessorFn: (row) =>
          row.nameDoc ? `${row.numDoc} ${row.nameDoc}` : "",
        id: "titulo",
        header: "TÍTULO",
        footer: "TÍTULO",
        size: 255,
      },
    ],
    []
  );

  const renderDetailPanelAcuerdos = ({ row }) => {
    const anexoRows = [];
    for (let i = 1; i <= 70; i++) {
      const pdfTitle = row.original[`titleAnexoPDF${i}`];
      const excelTitle = row.original[`titleAnexoEXCEL${i}`];
      if (pdfTitle) {
        anexoRows.push({
          key: `pdf-${i}`,
          title: pdfTitle.toUpperCase(),
          url: `${baseUrl}${year}/${row.original.id}.${i}.pdf`,
          type: "pdf",
        });
      }
      if (excelTitle) {
        anexoRows.push({
          key: `excel-${i}`,
          title: excelTitle.toUpperCase(),
          url: `${baseUrl}${year}/${row.original.id}.${i}.xlsx`,
          type: "excel",
        });
      }
    }

    return (
      <AcuerdosDetailContainer>
        <AcuerdosDetailSection title="ACUERDO">
          <AcuerdosDetailRow
            title={`${row.original.typeDoc} ${row.original.numDoc} ${
              row.original.nameDoc || ""
            }`.trim()}
            url={row.original.link ? `${baseUrl}${year}/${row.original.id}.pdf` : null}
            isMain={true}
          />
        </AcuerdosDetailSection>

        {anexoRows.length > 0 && (
          <AcuerdosDetailSection title="ANEXO">
            {anexoRows.map((item) => (
              <AcuerdosDetailRow
                key={item.key}
                title={item.title}
                url={item.url}
                type={item.type}
              />
            ))}
          </AcuerdosDetailSection>
        )}
      </AcuerdosDetailContainer>
    );
  };

  const tableAcuerdos = useCompleteTableConfig(
    data,
    columns,
    renderDetailPanelAcuerdos
  );

  return (
    <>
      <div className="margin-bottom-2">
        <Breadcrumbs
          path={[
            { label: "Acuerdos INE", url: "/AcuerdosINE" },
            { label: `Acuerdos INE ${year}` },
          ]}
        />
        <TitlePages title="Acuerdos INE" subTitle={`Acuerdos INE ${year}`} />
        <Expandible />
        <MaterialReactTable table={tableAcuerdos} />
      </div>
    </>
  );
};

export default AcuerdosTableINE;

FileLink.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["pdf", "excel"]).isRequired,
};

TableRow.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["pdf", "excel"]).isRequired,
};

AcuerdosTableINE.propTypes = {
  year: PropTypes.string.isRequired,
};
